import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import SendMessageModal from '../Messaging/SendMessageModal';
import SessionBookingModal from '../Booking/SessionBookingModal';

const ProviderListing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    skill: '',
    expertise: '',
    language: '',
    maxRate: '',
    minRate: '',
    minRating: '',
    minReviews: '',
    timezone: '',
    availableDate: '',
    ageBracket: '',
    gender: ''
  });

  const skillOptions = [
    'Interview Preparation',
    'Public Speaking',
    'Dating/Flirting',
    'Business Communication',
    'Language Practice',
    'Social Skills',
    'Presentation Skills',
    'Networking',
    'Confidence Building',
    'Accent Training'
  ];

  const expertiseOptions = [
    'Technology', 'Finance', 'Marketing', 'Sales', 'Healthcare',
    'Education', 'Law', 'Engineering', 'Design', 'Consulting',
    'Real Estate', 'Retail', 'Hospitality', 'Media', 'Non-profit'
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 
    'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic',
    'Russian', 'Hindi', 'Dutch', 'Swedish', 'Norwegian'
  ];

  const timezoneOptions = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
    'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Zurich', 'Europe/Dublin',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Seoul', 'Asia/Hong_Kong', 'Asia/Singapore',
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Bangkok', 'Asia/Manila', 'Asia/Jakarta',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
    'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Mexico_City',
    'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos'
  ];

  const ageBracketOptions = [
    '18-24', '25-34', '35-44', '45-54', '55-64', '65+'
  ];

  const genderOptions = [
    'Male', 'Female'
  ];

  useEffect(() => {
    fetchProviders();
    if (currentUser) {
      fetchCurrentUserData();
    }
  }, [currentUser]);

  const fetchProviders = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('userType', 'in', ['provider', 'both']),
        where('isProfileComplete', '==', true),
        where('emailVerified', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const providersData = [];
      
      querySnapshot.forEach((doc) => {
        if (currentUser && doc.id === currentUser.uid) {
          return;
        }
        
        const providerData = {
          id: doc.id,
          ...doc.data()
        };
        providersData.push(providerData);
      });
      
      setProviders(providersData);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUserData = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setCurrentUserData({ id: userDoc.id, ...userDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching current user data:', error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      search: '',
      skill: '',
      expertise: '',
      language: '',
      maxRate: '',
      minRate: '',
      minRating: '',
      minReviews: '',
      timezone: '',
      availableDate: '',
      ageBracket: '',
      gender: ''
    });
  };

  const handleBookSession = (e, providerId) => {
    e.stopPropagation();
    if (!currentUser) {
      const currentPath = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }
    const provider = providers.find(p => p.id === providerId);
    setSelectedProvider(provider);
    setShowBookingModal(true);
  };

  const handleSendMessage = (e, provider) => {
    e.stopPropagation();
    if (!currentUser) {
      const currentPath = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }
    setSelectedProvider(provider);
    setShowMessageModal(true);
  };

  const getAvailabilityStatus = (provider) => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    const currentDay = now.getDay();
    const today = now.toISOString().split('T')[0];
    
    // Check one-off availability for today
    if (provider.oneOffAvailability) {
      const todaySlot = provider.oneOffAvailability.find(slot => 
        slot.isActive && 
        slot.date === today &&
        currentTime >= slot.startTime &&
        currentTime <= slot.endTime
      );
      if (todaySlot) {
        return { isAvailable: true, type: 'one-off', nextSlot: todaySlot };
      }
    }
    
    // Check recurring availability for today
    let recurringSlots = [];
    
    if (provider.weeklyAvailability) {
      const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayKey = dayMap[currentDay];
      const dayData = provider.weeklyAvailability[dayKey];
      
      if (dayData && dayData.available) {
        recurringSlots = dayData.timeSlots.map(slot => ({
          ...slot,
          dayOfWeek: currentDay,
          isActive: true
        }));
      }
    } else if (provider.recurringAvailability) {
      recurringSlots = provider.recurringAvailability;
    }
    
    const todaySlot = recurringSlots.find(slot => 
      slot.isActive && 
      slot.dayOfWeek === currentDay &&
      currentTime >= slot.startTime &&
      currentTime <= slot.endTime
    );
    
    if (todaySlot) {
      return { isAvailable: true, type: 'recurring', nextSlot: todaySlot };
    }
    
    return { isAvailable: false, nextSlot: null };
  };

  const filteredProviders = providers.filter(provider => {
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const fullName = `${provider.firstName || ''} ${provider.lastName || ''}`.toLowerCase();
      const bio = (provider.bio || '').toLowerCase();
      const experience = (provider.experience || '').toLowerCase();
      
      const matchesSearch = fullName.includes(searchTerm) || 
                           bio.includes(searchTerm) || 
                           experience.includes(searchTerm);
      
      if (!matchesSearch) return false;
    }
    
    if (filters.skill && !provider.skills?.includes(filters.skill)) return false;
    if (filters.expertise && !provider.expertise?.includes(filters.expertise)) return false;
    if (filters.language && !provider.languages?.includes(filters.language)) return false;
    if (filters.minRate && provider.hourlyRate < parseFloat(filters.minRate)) return false;
    if (filters.maxRate && provider.hourlyRate > parseFloat(filters.maxRate)) return false;
    if (filters.minRating && provider.averageRating < parseFloat(filters.minRating)) return false;
    if (filters.minReviews && provider.reviewCount < parseInt(filters.minReviews)) return false;
    if (filters.timezone && provider.timezone !== filters.timezone) return false;
    if (filters.ageBracket && provider.ageBracket !== filters.ageBracket) return false;
    if (filters.gender && provider.gender !== filters.gender) return false;
    
    return true;
  });

  if (loading) {
    return (
      <div className="provider-listing">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Finding amazing providers for you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-listing">
      <div className="provider-listing-header">
        <h1>Find a Provider</h1>
        <p>Connect with expert conversation partners to practice and improve your communication skills</p>
      </div>

      {/* Search and Filters */}
      <div className="filters-container">
        <div className="search-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name, bio, or experience..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="form-input search-input"
            />
          </div>
          
          <div className="availability-search">
            <label>Search by Availability</label>
            <div className="availability-inputs">
              <input
                type="date"
                value={filters.availableDate}
                onChange={(e) => handleFilterChange('availableDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="form-input"
              />
              {filters.availableDate && (
                <button
                  onClick={() => handleFilterChange('availableDate', '')}
                  className="btn-secondary clear-btn"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="filters-grid">
          <div className="filter-group">
            <label>Skills</label>
            <select 
              value={filters.skill} 
              onChange={(e) => handleFilterChange('skill', e.target.value)}
              className="form-select"
            >
              <option value="">All Skills</option>
              {skillOptions.map(skill => (
                <option key={skill} value={skill}>{skill}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Expertise</label>
            <select 
              value={filters.expertise} 
              onChange={(e) => handleFilterChange('expertise', e.target.value)}
              className="form-select"
            >
              <option value="">All Expertise</option>
              {expertiseOptions.map(expertise => (
                <option key={expertise} value={expertise}>{expertise}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Language</label>
            <select 
              value={filters.language} 
              onChange={(e) => handleFilterChange('language', e.target.value)}
              className="form-select"
            >
              <option value="">All Languages</option>
              {languageOptions.map(language => (
                <option key={language} value={language}>{language}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Min Rating</label>
            <select 
              value={filters.minRating} 
              onChange={(e) => handleFilterChange('minRating', e.target.value)}
              className="form-select"
            >
              <option value="">Any Rating</option>
              <option value="4">4+ Stars</option>
              <option value="4.5">4.5+ Stars</option>
              <option value="5">5 Stars</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Min Rate ($)</label>
            <input
              type="number"
              placeholder="0"
              value={filters.minRate}
              onChange={(e) => handleFilterChange('minRate', e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label>Max Rate ($)</label>
            <input
              type="number"
              placeholder="100"
              value={filters.maxRate}
              onChange={(e) => handleFilterChange('maxRate', e.target.value)}
              className="form-input"
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearAllFilters} className="btn-secondary">
            Clear All Filters
          </button>
          <span className="results-count">
            {filteredProviders.length} provider{filteredProviders.length !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>

      {/* Provider Results */}
      <div className="providers-section">
        {filteredProviders.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>No providers found</h3>
            <p>Try adjusting your filters to find more providers</p>
            <button onClick={clearAllFilters} className="btn-primary">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="providers-grid">
            {filteredProviders.map(provider => {
              const availabilityStatus = getAvailabilityStatus(provider);
              
              return (
                <div key={provider.id} className="provider-card">
                  <div className="provider-header-compact">
                    <div className="provider-main-info">
                      <div className="provider-avatar-small">
                        {provider.profilePicture ? (
                          <img src={provider.profilePicture} alt={`${provider.firstName} ${provider.lastName}`} />
                        ) : (
                          <div className="avatar-placeholder-small">
                            {provider.firstName?.[0]}{provider.lastName?.[0]}
                          </div>
                        )}
                      </div>
                      <div className="provider-basic-info">
                        <h3 className="provider-name-compact">{provider.firstName} {provider.lastName}</h3>
                        <div className="provider-rating-compact">
                          <span className="rating-stars-small">
                            {'★'.repeat(Math.floor(provider.averageRating || 0))}
                            {'☆'.repeat(5 - Math.floor(provider.averageRating || 0))}
                          </span>
                          <span className="rating-text-small">
                            {provider.averageRating?.toFixed(1) || 'No rating'} ({provider.reviewCount || 0})
                          </span>
                        </div>
                        <div className="provider-location-compact">
                          🌍 {provider.timezone?.replace('_', ' ') || 'Not specified'}
                        </div>
                      </div>
                    </div>
                    <div className="provider-rate-availability">
                      <div className="provider-rate-top">
                        <span className="rate-amount-small">${provider.hourlyRate || 0}/hr</span>
                      </div>
                      <div className="provider-availability-small">
                        {availabilityStatus.isAvailable ? (
                          <div className="availability-status available small">
                            <span className="status-indicator available small"></span>
                            <span className="status-text small">Available</span>
                          </div>
                        ) : (
                          <div className="availability-status unavailable small">
                            <span className="status-indicator unavailable small"></span>
                            <span className="status-text small">Check times</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="provider-bio">
                    <p>{(provider.bio || 'No bio available').substring(0, 100)}{provider.bio?.length > 100 ? '...' : ''}</p>
                  </div>

                  <div className="provider-details">
                    <div className="provider-skills">
                      <h4>Skills</h4>
                      <div className="skills-list">
                        {provider.skills?.slice(0, 3).map(skill => (
                          <span key={skill} className="skill-tag">{skill}</span>
                        ))}
                        {provider.skills?.length > 3 && (
                          <span className="skill-tag more">+{provider.skills.length - 3}</span>
                        )}
                      </div>
                    </div>

                    {provider.expertise && provider.expertise.length > 0 && (
                      <div className="provider-expertise-compact">
                        <h4>Expertise</h4>
                        <p>{provider.expertise.slice(0, 2).join(', ')}{provider.expertise.length > 2 ? ` +${provider.expertise.length - 2} more` : ''}</p>
                      </div>
                    )}

                    {provider.languages && provider.languages.length > 0 && (
                      <div className="provider-languages-compact">
                        <h4>Languages</h4>
                        <p>{provider.languages.slice(0, 3).join(', ')}{provider.languages.length > 3 ? ` +${provider.languages.length - 3} more` : ''}</p>
                      </div>
                    )}
                  </div>

                  <div className="provider-actions">
                    <button 
                      className="book-session-btn"
                      onClick={(e) => handleBookSession(e, provider.id)}
                    >
                      {currentUser ? 'Book Session' : 'Sign In to Book'}
                    </button>
                    <button 
                      className="view-profile-btn-compact"
                      onClick={() => navigate(`/provider/${provider.username || provider.id}`)}
                    >
                      View Profile
                    </button>
                    <button 
                      className="message-btn-compact" 
                      onClick={(e) => handleSendMessage(e, provider)}
                    >
                      {currentUser ? 'Message' : 'Sign In'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SendMessageModal
        isOpen={showMessageModal}
        onClose={() => {
          setShowMessageModal(false);
          setSelectedProvider(null);
        }}
        recipient={selectedProvider}
        currentUserData={currentUserData}
      />

      {showBookingModal && selectedProvider && (
        <SessionBookingModal
          provider={selectedProvider}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedProvider(null);
          }}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default ProviderListing;